import React, {
  createContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useContext,
  useState,
} from "react";
import { createClient, OAuthStrategy } from "@wix/sdk";
import { members, authentication } from "@wix/members";
import { plans, orders } from "@wix/pricing-plans";
import { redirects } from "@wix/redirects";
import { auth, firestore, functions } from "../FirebaseConfig";
import {
  signInWithCustomToken,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  getIdTokenResult,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

// Create the context
const CombinedAuthContext = createContext(null);
export const useAuth = () => useContext(CombinedAuthContext);

/**
 * Safely parses JSON from localStorage with proper error handling
 * @param {string} key - localStorage key
 * @param {any} defaultValue - fallback value if parsing fails
 * @returns {any} - parsed value or default
 */
const safeGetFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (!item || item === null || item === "undefined" || item === undefined) {
      return defaultValue;
    }
    return JSON.parse(item);
  } catch (error) {
    console.error(`Error parsing ${key} from localStorage:`, error);
    return defaultValue;
  }
};

// Define initial authentication state
const initialState = {
  wixUser: null,
  wixTokens: safeGetFromLocalStorage("wix_session", null),
  wixLoading: true,
  wixError: null,
  firebaseUser: null,
  firebaseToken: safeGetFromLocalStorage("firebase_session", null),
  firebaseLoading: true,
  firebaseError: null,
  firebaseConnected: false,
  tokenExpiry: null,
  connectionTime: null,
};

// Reducer to update only the critical parts of the auth state
function reducer(state, action) {
  switch (action.type) {
    case "SET_WIX_USER":
      return { ...state, wixUser: action.payload };
    case "SET_WIX_TOKENS":
      return { ...state, wixTokens: action.payload };
    case "SET_WIX_LOADING":
      return { ...state, wixLoading: action.payload };
    case "SET_WIX_ERROR":
      return { ...state, wixError: action.payload };
    case "SET_FIREBASE_USER":
      return { ...state, firebaseUser: action.payload };
    case "SET_FIREBASE_TOKENS":
      return { ...state, firebaseTokens: action.payload };
    case "SET_FIREBASE_LOADING":
      return { ...state, firebaseLoading: action.payload };
    case "SET_FIREBASE_ERROR":
      return { ...state, firebaseError: action.payload };
    case "SET_FIREBASE_CONNECTED":
      return { ...state, firebaseConnected: action.payload };
    case "SET_TOKEN_EXPIRY":
      return { ...state, tokenExpiry: action.payload };
    case "SET_CONNECTION_TIME":
      return { ...state, connectionTime: action.payload };
    default:
      return state;
  }
}

const CLIENT_ID = process.env.REACT_APP_WIX_HEADLESS_CLIENT_ID;

export const CombinedAuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    // wixUser,
    wixTokens,
    //wixLoading,
    // wixError,
    // firebaseUser,
    // firebaseToken,
    // firebaseLoading,
    // firebaseError,
    // firebaseConnected,
    // tokenExpiry,
    // connectionTime,
  } = state;
  // const { wixLoading } = state; // Example if wixLoading IS needed for logic checks

  // Local state to track if initial load is done to prevent loops
  const [initialAuthDone, setInitialAuthDone] = useState(false);

  // Use a ref to signal component mount status (for cancelling async updates)
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Refs to break the initAuth feedback loop. The initAuth useEffect should
  // only retrigger when `client` changes (now stable). Anything else it needs
  // (callbacks that close over state, current state values, the auth-done
  // flag) is read through these refs so the effect's dep array stays minimal.
  const stateRef = useRef(state);
  const forceLogoutRef = useRef(null);
  const authenticateWithFirebaseRef = useRef(null);
  const initialAuthDoneRef = useRef(initialAuthDone);
  useEffect(() => {
    stateRef.current = state;
    initialAuthDoneRef.current = initialAuthDone;
  });

  // Create the Wix SDK client ONCE on mount. Token rotation is propagated via
  // client.auth.setTokens() in the effect below — recreating the client each
  // time tokens change caused initAuth to retrigger and feedback-loop.
  const client = useMemo(() => {
    console.log(
      "[DEBUG] [CombinedAuthContext] Creating/Recreating Wix SDK client."
    );
    return createClient({
      modules: { authentication, members, plans, orders, redirects },
      auth: OAuthStrategy({
        clientId: CLIENT_ID,
        tokens: wixTokens, // Initial tokens; updates flow through setTokens.
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount-only: stable identity prevents initAuth feedback loop.

  // Keep the Wix SDK client's tokens in sync with state without recreating it.
  useEffect(() => {
    if (client && wixTokens) {
      try {
        client.auth.setTokens(wixTokens);
      } catch (e) {
        console.warn(
          "[DEBUG] [CombinedAuthContext] client.auth.setTokens failed:",
          e
        );
      }
    }
  }, [client, wixTokens]);

  // Helper to safely log objects (does not affect state)
  const safeLogObject = useCallback((label, obj) => {
    try {
      const getCircularReplacer = () => {
        const seen = new WeakSet();
        return (key, value) => {
          if (typeof value === "object" && value !== null) {
            if (seen.has(value)) {
              return "[Circular Reference]";
            }
            seen.add(value);
          }
          return value;
        };
      };

      const redactSensitiveInfo = (obj) => {
        if (!obj) return obj;
        const copy = { ...obj };
        return copy;
      };

      const safeObj = redactSensitiveInfo(obj);
      console.log(
        `[DEBUG] [CombinedAuthContext] ${label}:`,
        JSON.stringify(safeObj, getCircularReplacer(), 2)
      );
    } catch (error) {
      console.log(
        `[DEBUG] [CombinedAuthContext] Error logging ${label}:`,
        error
      );
    }
  }, []);

  // Force logout (clears state and storage)
  const forceLogout = useCallback(() => {
    console.log("[DEBUG] [CombinedAuthContext] forceLogout called.");
    localStorage.removeItem("wix_session");
    localStorage.removeItem("firebase_session");
    localStorage.removeItem("oauthRedirectData");
    sessionStorage.removeItem("oauth_callback_processed");
    sessionStorage.removeItem("token_redirect_done");
    sessionStorage.removeItem("token_processed");

    // Access state directly here instead of relying on potentially stale closure variables
    if (
      state.wixTokens !== null ||
      state.wixUser !== null ||
      state.firebaseUser !== null
    ) {
      dispatch({ type: "SET_WIX_TOKENS", payload: null });
      dispatch({ type: "SET_WIX_USER", payload: null });
      dispatch({ type: "SET_FIREBASE_USER", payload: null });
      dispatch({ type: "SET_FIREBASE_CONNECTED", payload: false });
      dispatch({ type: "SET_FIREBASE_TOKENS", payload: null });
    }

    try {
      if (auth.currentUser) {
        firebaseSignOut(auth);
      }
    } catch (e) {
      console.error("Error signing out from Firebase during force logout:", e);
    }
  }, [state.wixTokens, state.wixUser, state.firebaseUser]); // Depend on specific state values used
  forceLogoutRef.current = forceLogout;

  // Authenticate with Firebase using Wix tokens and user data
  const authenticateWithFirebase = useCallback(
    async (userOverride = null, wixTokenOverride = null) => {
      let firebaseToken;
      const currentUser = userOverride || state.wixUser;
      const currentTokens =
        wixTokenOverride || state.wixTokens || client.auth.getTokens();
      if (!currentUser || !currentTokens) {
        dispatch({ type: "SET_FIREBASE_CONNECTED", payload: false });
        dispatch({ type: "SET_FIREBASE_LOADING", payload: false });
        //safeLogObject("authenticateWithFirebase() currentUser", currentUser);
        // safeLogObject(
        //   "authenticateWithFirebase() currentTokens",
        //   currentTokens
        // );
        return;
      }
      dispatch({ type: "SET_FIREBASE_LOADING", payload: true });

      //safeLogObject("Full Wix User Structure", currentUser);

      // Extract user info
      const email = currentUser.member?.loginEmail || currentUser.member?.email;
      let displayName =
        currentUser.member?.profile?.nickname ||
        (currentUser.member?.contact?.firstName &&
        currentUser.member?.contact?.lastName
          ? `${currentUser.member.contact.firstName} ${currentUser.member.contact.lastName}`
          : email
          ? email.split("@")[0]
          : "Member");
      let wixMemberId =
        currentUser.member?._id || currentUser.member?.contactId;

      if (!email || !wixMemberId) {
        if (currentUser.isAuthenticated) {
          displayName = displayName || "Pirate Member";
          dispatch({
            type: "SET_FIREBASE_ERROR",
            payload: "Using fallback user information",
          });
        } else {
          dispatch({
            type: "SET_FIREBASE_ERROR",
            payload: "Missing required Wix user information",
          });
          dispatch({ type: "SET_FIREBASE_CONNECTED", payload: false });
          dispatch({ type: "SET_FIREBASE_LOADING", payload: false });
          return;
        }
      }

      // Extract token values
      let accessTokenValue;
      let refreshTokenValue;
      if (currentTokens.accessToken) {
        if (typeof currentTokens.accessToken === "string") {
          accessTokenValue = currentTokens.accessToken;
        } else if (typeof currentTokens.accessToken === "object") {
          accessTokenValue =
            currentTokens.accessToken.value ||
            JSON.stringify(currentTokens.accessToken);
        }
      }
      if (currentTokens.refreshToken) {
        if (typeof currentTokens.refreshToken === "string") {
          refreshTokenValue = currentTokens.refreshToken;
        } else if (typeof currentTokens.refreshToken === "object") {
          refreshTokenValue = currentTokens.refreshToken.value;
        }
      }
      if (!accessTokenValue) {
        dispatch({
          type: "SET_FIREBASE_ERROR",
          payload: "Missing Wix access token value",
        });
        dispatch({ type: "SET_FIREBASE_CONNECTED", payload: false });
        dispatch({ type: "SET_FIREBASE_LOADING", payload: false });
        return;
      }

      try {
        const createFirebaseToken = httpsCallable(
          functions,
          "createFirebaseToken"
        );
        const callData = {
          accessToken: String(accessTokenValue),
          refreshToken: refreshTokenValue ? String(refreshTokenValue) : "",
          email: email ? String(email) : "",
          displayName: displayName ? String(displayName) : "",
          wixMemberId: wixMemberId ? String(wixMemberId) : "",
        };

        const result = await createFirebaseToken(callData);
        // safeLogObject(
        //   "authenticateWithFirebase() createFirebaseToken() result",
        //   result
        // );
        await signInWithCustomToken(auth, result.data.token);
        const tokenResult = await getIdTokenResult(auth.currentUser);
        // safeLogObject(
        //   "authenticateWithFirebase() signInWithCustomToken() tokenResult",
        //   tokenResult
        // );
        if (tokenResult) {
          localStorage.setItem("firebase_session", JSON.stringify(tokenResult));
        }
        firebaseToken = tokenResult;
        const expiryTime = new Date(tokenResult.expirationTime);
        dispatch({ type: "SET_TOKEN_EXPIRY", payload: expiryTime });
        const now = new Date();
        dispatch({ type: "SET_CONNECTION_TIME", payload: now });
        // Read existing doc so we only seed baseline membership fields on first
        // login. Returning users keep whatever tier they already have.
        const userDocRef = doc(firestore, "users", auth.currentUser.uid);
        let existingUserData = null;
        try {
          const existingSnap = await getDoc(userDocRef);
          existingUserData = existingSnap.exists() ? existingSnap.data() : null;
        } catch (readErr) {
          console.warn(
            "[CombinedAuthContext] Could not read existing user doc:",
            readErr?.message
          );
        }

        const isFirstLogin = !existingUserData;

        // Pull signup context from sessionStorage so we can grant the right
        // free starting tier (Deckhand for travelers, Crew Listing for
        // businesses) and remember the original signup intent.
        let signupCtx = null;
        if (isFirstLogin) {
          try {
            const raw = sessionStorage.getItem("signupContext");
            if (raw) {
              const parsed = JSON.parse(raw);
              const audience = ["traveler", "business", "realestate"].includes(
                parsed?.audience
              )
                ? parsed.audience
                : "traveler";
              signupCtx = {
                audience,
                tier:
                  typeof parsed?.tier === "string" && parsed.tier
                    ? parsed.tier.slice(0, 32)
                    : null,
                intent:
                  typeof parsed?.intent === "string" && parsed.intent
                    ? parsed.intent.slice(0, 64)
                    : null,
                acceptedTermsVersion:
                  Number.isFinite(parsed?.acceptedTermsVersion)
                    ? parsed.acceptedTermsVersion
                    : null,
                acceptedTermsAt:
                  Number.isFinite(parsed?.acceptedTermsAt)
                    ? parsed.acceptedTermsAt
                    : null,
              };
            }
          } catch (ctxErr) {
            // Ignore malformed signup context.
            signupCtx = null;
          }
        }

        const baseUpdate = {
          email,
          displayName,
          wixMemberId,
          lastLogin: serverTimestamp(),
          wixAuthenticated: true,
        };

        const seedUpdate = isFirstLogin
          ? {
              createdAt: serverTimestamp(),
              // Free starting tier: Deckhand for travelers/realestate visitors,
              // Crew Listing for businesses. Paid tiers are still resolved
              // from Wix memberOrders; this only sets the baseline floor.
              freeMembershipTier:
                signupCtx?.audience === "business"
                  ? "crew"
                  : "deckhand",
              signupAudience: signupCtx?.audience || "traveler",
              signupTier: signupCtx?.tier || null,
              signupIntent: signupCtx?.intent || null,
              signupSource: "wix-oauth",
              legal: {
                acceptedTermsVersion: signupCtx?.acceptedTermsVersion ?? null,
                acceptedTermsAt: signupCtx?.acceptedTermsAt
                  ? new Date(signupCtx.acceptedTermsAt)
                  : null,
              },
            }
          : {};

        await setDoc(userDocRef, { ...baseUpdate, ...seedUpdate }, { merge: true });

        if (isFirstLogin) {
          try {
            sessionStorage.removeItem("signupContext");
          } catch (_e) {
            /* noop */
          }
        }
        dispatch({ type: "SET_FIREBASE_CONNECTED", payload: true });
        dispatch({ type: "SET_FIREBASE_ERROR", payload: null });
      } catch (error) {
        dispatch({ type: "SET_FIREBASE_ERROR", payload: error.message });
        dispatch({ type: "SET_FIREBASE_CONNECTED", payload: false });
      } finally {
        dispatch({ type: "SET_FIREBASE_LOADING", payload: false });
      }
      return firebaseToken;
    },
    [state.wixUser, state.wixTokens, client]
  );
  authenticateWithFirebaseRef.current = authenticateWithFirebase;

  // Refresh the Firebase token when needed
  const refreshFirebaseToken = useCallback(
    async (userOverride = null) => {
      const currentUser = userOverride || state.wixUser;
      if (!currentUser || !state.wixTokens) return;
      const email = currentUser.member?.loginEmail || currentUser.member?.email;
      const wixMemberId =
        currentUser.member?._id || currentUser.member?.contactId;
      const userEmail = email || "user@example.com";
      const userId = wixMemberId || `wix_${Date.now()}`;
      let accessTokenValue;
      if (state.wixTokens.accessToken) {
        if (typeof state.wixTokens.accessToken === "string") {
          accessTokenValue = state.wixTokens.accessToken;
        } else if (typeof state.wixTokens.accessToken === "object") {
          accessTokenValue =
            state.wixTokens.accessToken.value ||
            JSON.stringify(state.wixTokens.accessToken);
        }
      }
      if (!accessTokenValue) return;
      try {
        const refreshTokenFn = httpsCallable(functions, "refreshFirebaseToken");
        const callData = {
          accessToken: String(accessTokenValue),
          email: String(userEmail),
          wixMemberId: String(userId),
        };
        const result = await refreshTokenFn(callData);
        await signInWithCustomToken(auth, result.data.token);
        const tokenResult = await getIdTokenResult(auth.currentUser);
        const expiryTime = new Date(tokenResult.expirationTime);
        dispatch({ type: "SET_TOKEN_EXPIRY", payload: expiryTime });
        dispatch({ type: "SET_FIREBASE_CONNECTED", payload: true });
        dispatch({ type: "SET_FIREBASE_ERROR", payload: null });
        return true;
      } catch (error) {
        dispatch({ type: "SET_FIREBASE_ERROR", payload: error.message });
        return false;
      }
    },
    [state.wixUser, state.wixTokens]
  );

  // Initialize authentication on mount
  useEffect(() => {
    let isRefreshing = false;

    async function initAuth() {
      if (!isMountedRef.current || !client || initialAuthDoneRef.current) {
        // Added initialAuthDone check
        // If initial auth is done, subsequent runs triggered by client/token changes are ok,
        // but we might want finer control if loop persists.
        // For now, just prevent re-running the *whole* initial check if already done once.
        // If initialAuthDone is true, it means we are likely in a re-run due to token refresh/login/logout.
        if (initialAuthDoneRef.current) {
          console.log(
            "[DEBUG] [CombinedAuthContext] initAuth: Skipping full re-check, initial auth done."
          );
          // We might still need to handle state updates based on token changes here if needed
          // For example, re-validating firebase connection if wixTokens changed significantly
          // But let's keep it simple first to stop the loop.
          return;
        }
        if (!client) {
          console.log(
            "[DEBUG] [CombinedAuthContext] initAuth: Client not ready."
          );
          return; // Wait for client
        }
        if (!isMountedRef.current) return;
      }

      console.log(
        "[DEBUG] [CombinedAuthContext] initAuth: Running Initial Check."
      );
      dispatch({ type: "SET_WIX_LOADING", payload: true });
      dispatch({ type: "SET_FIREBASE_LOADING", payload: true });

      try {
        const currentTokens = client.auth.getTokens();
        console.log(
          "[DEBUG] [CombinedAuthContext] initAuth: Tokens from client",
          currentTokens
            ? {
                hasAccessToken: !!currentTokens.accessToken?.value,
                hasRefreshToken: !!currentTokens.refreshToken?.value,
              }
            : null
        );

        const hasValidAccessToken =
          typeof currentTokens?.accessToken?.value === "string" &&
          currentTokens.accessToken.value.length > 0;
        const hasValidRefreshToken =
          typeof currentTokens?.refreshToken?.value === "string" &&
          currentTokens.refreshToken.value.length > 0;

        if (hasValidAccessToken && hasValidRefreshToken) {
          client.auth.setTokens(currentTokens);
          try {
            console.log(
              "[DEBUG] [CombinedAuthContext] initAuth: Attempting to get current member."
            );
            const member = await client.members.getCurrentMember({
              fieldsets: ["FULL"],
            });

            if (isMountedRef.current) {
              dispatch({ type: "SET_WIX_USER", payload: member });
              console.log(
                "[DEBUG] [CombinedAuthContext] initAuth: Member found, authenticating Firebase."
              );
              const fbToken = await authenticateWithFirebaseRef.current(
                member,
                currentTokens
              );
              if (isMountedRef.current) {
                localStorage.setItem(
                  "firebase_session",
                  JSON.stringify(fbToken)
                );
                dispatch({ type: "SET_FIREBASE_TOKENS", payload: fbToken });
              }
            }
          } catch (memberError) {
            if (!isMountedRef.current) return;
            console.error(
              "[DEBUG] [CombinedAuthContext] initAuth: Error getting member:",
              memberError
            );

            const statusCode =
              memberError.status || memberError.details?.httpStatusCode;
            const errorCode = memberError.details?.applicationError?.code;

            if (
              (statusCode === 403 ||
                errorCode === "PERMISSION_DENIED" ||
                errorCode === "INVALID_ACCESS_TOKEN") &&
              currentTokens.refreshToken?.value
            ) {
              console.log(
                `[DEBUG] [CombinedAuthContext] initAuth: Auth error (${statusCode}/${errorCode}), attempting token refresh.`
              );
              isRefreshing = true;
              try {
                dispatch({ type: "SET_WIX_LOADING", payload: true }); // Keep loading during refresh
                dispatch({ type: "SET_FIREBASE_LOADING", payload: true });

                const refreshResult = await client.auth.refreshTokens(
                  currentTokens.refreshToken.value
                );
                if (refreshResult && isMountedRef.current) {
                  console.log(
                    "[DEBUG] [CombinedAuthContext] initAuth: Token refresh successful via initAuth."
                  );
                  localStorage.setItem(
                    "wix_session",
                    JSON.stringify(refreshResult)
                  );
                  dispatch({ type: "SET_WIX_TOKENS", payload: refreshResult });
                  // Don't set initialAuthDone here, let the re-run triggered by SET_WIX_TOKENS complete the process
                  return; // Exit this run
                } else if (isMountedRef.current) {
                  console.log(
                    "[DEBUG] [CombinedAuthContext] initAuth: Token refresh failed, logging out."
                  );
                  isRefreshing = false;
                  forceLogoutRef.current?.();
                }
              } catch (refreshError) {
                console.error(
                  "[DEBUG] [CombinedAuthContext] initAuth: Exception during token refresh:",
                  refreshError
                );
                isRefreshing = false;
                if (isMountedRef.current) forceLogoutRef.current?.();
              }
            } else if (isMountedRef.current) {
              console.log(
                "[DEBUG] [CombinedAuthContext] initAuth: Unhandled member error or no refresh token, clearing session."
              );
              forceLogoutRef.current?.();
            }
          } finally {
            if (isMountedRef.current && !isRefreshing) {
              dispatch({ type: "SET_FIREBASE_LOADING", payload: false });
            }
          }
        } else {
          console.log(
            "[DEBUG] [CombinedAuthContext] initAuth: No valid Wix access/refresh tokens found."
          );
          if (isMountedRef.current) {
            // Use the latest state via stateRef to avoid recreating the effect.
            if (stateRef.current.wixTokens) {
              console.log(
                "[DEBUG] [CombinedAuthContext] initAuth: Invalid tokens found in state, forcing logout."
              );
              forceLogoutRef.current?.();
            } else {
              // No tokens initially, just ensure logged out state
              dispatch({ type: "SET_WIX_USER", payload: null });
              dispatch({ type: "SET_FIREBASE_USER", payload: null });
              dispatch({ type: "SET_FIREBASE_CONNECTED", payload: false });
              dispatch({ type: "SET_FIREBASE_TOKENS", payload: null });
            }
          }
        }
      } catch (initError) {
        console.error(
          "[DEBUG] [CombinedAuthContext] initAuth: General initialization error:",
          initError
        );
        if (isMountedRef.current) {
          dispatch({ type: "SET_WIX_ERROR", payload: initError });
        }
      } finally {
        if (isMountedRef.current) {
          // Set loading false only if not refreshing
          if (!isRefreshing) {
            console.log(
              "[DEBUG] [CombinedAuthContext] initAuth: Finalizing initial auth check."
            );
            dispatch({ type: "SET_WIX_LOADING", payload: false });
            dispatch({ type: "SET_FIREBASE_LOADING", payload: false });
            setInitialAuthDone(true); // Mark initial auth as done
          } else {
            console.log(
              "[DEBUG] [CombinedAuthContext] initAuth: Exiting finally block, refresh triggered."
            );
          }
        }
      }
    }
    initAuth();
    // Refs (forceLogoutRef, authenticateWithFirebaseRef, stateRef,
    // initialAuthDoneRef) are used inside initAuth so they are intentionally
    // omitted from deps. Only re-run when the (now stable) client identity
    // changes — i.e. effectively once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      dispatch({ type: "SET_FIREBASE_USER", payload: user });
      dispatch({ type: "SET_FIREBASE_CONNECTED", payload: !!user });
      if (user) {
        getIdTokenResult(user)
          .then((tokenResult) => {
            const expiryTime = new Date(tokenResult.expirationTime);
            dispatch({ type: "SET_TOKEN_EXPIRY", payload: expiryTime });
          })
          .catch((error) => {
            console.error("Error getting token result:", error);
          });
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // Periodically check token expiry and trigger refresh if needed
  useEffect(() => {
    if (!state.tokenExpiry) return;
    const checkExpiry = () => {
      const now = new Date();
      const expiryTime = new Date(state.tokenExpiry);
      const timeUntilExpiry = expiryTime - now;
      if (timeUntilExpiry < 5 * 60 * 1000) {
        refreshFirebaseToken();
      }
    };
    checkExpiry();
    const interval = setInterval(checkExpiry, 60000);
    return () => {
      clearInterval(interval);
    };
  }, [state.tokenExpiry, refreshFirebaseToken]);

  // Logout function—signs out from Firebase and Wix
  const logout = useCallback(async () => {
    try {
      try {
        await firebaseSignOut(auth);
        dispatch({ type: "SET_FIREBASE_USER", payload: null });
        dispatch({ type: "SET_FIREBASE_CONNECTED", payload: false });
      } catch (firebaseError) {
        console.error("Firebase sign out error:", firebaseError);
      }
      const currentUrl = window.location.href;
      try {
        const { logoutUrl } = await client.auth.logout(currentUrl);
        localStorage.removeItem("wix_session");
        localStorage.removeItem("firebase_session");
        sessionStorage.removeItem("oauth_callback_processed");
        dispatch({ type: "SET_WIX_TOKENS", payload: null });
        dispatch({ type: "SET_WIX_USER", payload: null });
        window.location = logoutUrl;
      } catch (apiError) {
        safeLogObject("Logout API Error", apiError);
        forceLogout();
      }
    } catch (logoutError) {
      safeLogObject("Complete Logout Error", logoutError);
      forceLogout();
    }
  }, [client, forceLogout, safeLogObject]);

  // Signup function—redirects to the Wix OAuth URL with sign-up intent.
  // Falls back to the standard login flow if the SDK does not expose a sign-up mode.
  const signup = useCallback(async () => {
    try {
      // Never run the create-account flow for someone already signed in. The
      // session clearing below is destructive and happens BEFORE Wix can reject
      // the create_account prompt for an existing member, so a stray call would
      // log the user out and surface an auth error.
      if (state.isAuthenticated) {
        console.warn("[CombinedAuthContext] signup() ignored — already authenticated.");
        return;
      }

      localStorage.removeItem("wix_session");
      localStorage.removeItem("firebase_session");
      localStorage.removeItem("oauthRedirectData");
      sessionStorage.removeItem("oauth_callback_processed");
      sessionStorage.removeItem("token_redirect_done");
      sessionStorage.removeItem("token_processed");

      const currentUrlOrigin = window.location.origin;
      const redirectUri = `${currentUrlOrigin}/login-callback`;
      const originalUrl = window.location.href.includes("/login-callback")
        ? currentUrlOrigin
        : window.location.href;

      const data = client.auth.generateOAuthData(redirectUri, originalUrl);
      localStorage.setItem("oauthRedirectData", JSON.stringify(data));

      // Request sign-up mode when supported by the Wix SDK
      const authUrlResult = await client.auth.getAuthUrl(data, { responseMode: 'query', prompt: 'create_account' }).catch(
        () => client.auth.getAuthUrl(data)
      );
      const authUrl = authUrlResult.authUrl;
      console.log("[DEBUG] [CombinedAuthContext] Redirecting to Wix Sign-Up URL:", authUrl);
      window.location.assign(authUrl);
    } catch (signupError) {
      console.error("[DEBUG] [CombinedAuthContext] Signup Error:", signupError);
      dispatch({ type: "SET_WIX_ERROR", payload: signupError });
    }
    // state.isAuthenticated must stay in deps — without it the guard above
    // reads a stale `false` from the first render and never fires.
  }, [client, state.isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Login function—redirects to the Wix OAuth URL
  const login = useCallback(async () => {
    try {
      // Clear relevant storage items
      localStorage.removeItem("wix_session");
      localStorage.removeItem("firebase_session");
      localStorage.removeItem("oauthRedirectData");
      sessionStorage.removeItem("oauth_callback_processed");
      sessionStorage.removeItem("token_redirect_done");
      sessionStorage.removeItem("token_processed");

      // Define URIs
      const currentUrlOrigin = window.location.origin;
      const redirectUri = `${currentUrlOrigin}/login-callback`;
      const originalUrl = window.location.href.includes("/login-callback")
        ? currentUrlOrigin // If already on callback, redirect back to home after login
        : window.location.href; // Otherwise, remember the current page

      const data = client.auth.generateOAuthData(redirectUri, originalUrl);
      safeLogObject("OAuth Data", data);
      localStorage.setItem("oauthRedirectData", JSON.stringify(data)); // Store data needed on return

      const { authUrl } = await client.auth.getAuthUrl(data);
      console.log(
        "[DEBUG] [CombinedAuthContext] Redirecting to Wix Auth URL:",
        authUrl
      );
      window.location.assign(authUrl); // Redirect the browser
    } catch (loginError) {
      console.error("[DEBUG] [CombinedAuthContext] Login Error:", loginError);
      dispatch({ type: "SET_WIX_ERROR", payload: loginError });
    }
  }, [client, safeLogObject]); // Added client, safeLogObject dependencies

  // Process the login callback from Wix
  const processLoginCallback = useCallback(async () => {
    console.log("[DEBUG] [CombinedAuthContext] processLoginCallback called.");
    try {
      const oauthRedirectData = JSON.parse(
        localStorage.getItem("oauthRedirectData") || "null"
      );
      if (!oauthRedirectData) {
        console.error(
          "[DEBUG] [CombinedAuthContext] Missing OAuth redirect data."
        );
        throw new Error("Missing OAuth redirect data");
      }

      const returnedOAuthData = client.auth.parseFromUrl(); // Use the client instance
      safeLogObject("Returned OAuth Data", returnedOAuthData);

      if (returnedOAuthData.error) {
        console.error(
          "[DEBUG] [CombinedAuthContext] OAuth Error from URL:",
          returnedOAuthData.errorDescription
        );
        throw new Error(
          returnedOAuthData.errorDescription || returnedOAuthData.error
        );
      }
      if (!returnedOAuthData.code || !returnedOAuthData.state) {
        console.error(
          "[DEBUG] [CombinedAuthContext] Missing code or state in returned data."
        );
        throw new Error("Incomplete authentication response received.");
      }

      console.log("[DEBUG] [CombinedAuthContext] Getting member tokens...");
      const newTokens = await client.auth.getMemberTokens(
        returnedOAuthData.code,
        returnedOAuthData.state,
        oauthRedirectData // Pass the original data for validation
      );
      console.log("[DEBUG] [CombinedAuthContext] Received new tokens.");
      //safeLogObject("New Tokens", newTokens);

      localStorage.setItem("wix_session", JSON.stringify(newTokens));
      localStorage.removeItem("oauthRedirectData"); // Clean up
      dispatch({ type: "SET_WIX_TOKENS", payload: newTokens }); // Update state, triggers client recreation
      client.auth.setTokens(newTokens); // Also update current client instance immediately

      console.log(
        "[DEBUG] [CombinedAuthContext] Getting member info after token exchange..."
      );
      // Use the *updated* client (or wait for re-render, but setting manually is safer)
      const member = await client.members.getCurrentMember({
        fieldsets: ["FULL"],
      });
      //safeLogObject("Member Data After Login", member);
      dispatch({ type: "SET_WIX_USER", payload: member });

      console.log(
        "[DEBUG] [CombinedAuthContext] Authenticating with Firebase..."
      );
      const fbToken = await authenticateWithFirebase(member, newTokens);
      // safeLogObject("Firebase Token After Login", fbToken);
      localStorage.setItem("firebase_session", JSON.stringify(fbToken));
      dispatch({ type: "SET_FIREBASE_TOKENS", payload: fbToken });
      dispatch({ type: "SET_FIREBASE_LOADING", payload: false }); // Firebase loading done

      return {
        success: true,
        redirectUri: oauthRedirectData.originalUri || "/",
      }; // Use originalUri stored earlier
    } catch (callbackError) {
      console.error(
        "[DEBUG] [CombinedAuthContext] processLoginCallback Error:",
        callbackError
      );
      safeLogObject("Callback Error", callbackError);
      // Optionally dispatch error to state
      // dispatch({ type: "SET_WIX_ERROR", payload: callbackError });
      return { success: false, error: callbackError.message, redirectUri: "/" };
    }
  }, [client, authenticateWithFirebase, safeLogObject]);

  // Add this function to your CombinedAuthContext.js
  const handleSessionToken = useCallback(
    async (sessionToken, fallbackEmail) => {
      try {
        if (!sessionToken)
          return { success: false, error: "No sessionToken provided" };

        dispatch({ type: "SET_WIX_LOADING", payload: true });

        // Instead of directly using the token, we need to exchange it for proper OAuth tokens
        try {
          // Option 1: Use the exchangeToken method if it exists in the Wix SDK
          if (typeof client.auth.getMemberTokensForDirectLogin === "function") {
            console.log("Using exchangeToken method for session token");
            const exchangeResult =
              await client.auth.getMemberTokensForDirectLogin(sessionToken);
            safeLogObject("Token exchange result", exchangeResult);

            if (exchangeResult && exchangeResult.accessToken) {
              // Store the proper tokens and continue with authentication
              localStorage.setItem(
                "wix_session",
                JSON.stringify(exchangeResult)
              );
              dispatch({ type: "SET_WIX_TOKENS", payload: exchangeResult });
              client.auth.setTokens(exchangeResult);
            }
          }
          // Option 2: Try to use the token with the memberTokens method
          else {
            console.log("Using memberTokens method for session token");
            // Try to contact Wix API to verify the member session
            // This approach might work if the token is a valid member session token
            const sessionData = {
              token: sessionToken,
              sessionToken: sessionToken,
            };

            // Configure client with the token
            client.auth.setTokens(sessionData);
            safeLogObject("Set session token", sessionData);
          }

          // Now try to get the member with the configured client
          const member = await client.members.getCurrentMember({
            fieldsets: ["FULL"],
          });

          safeLogObject("member", member);

          if (member) {
            dispatch({ type: "SET_WIX_USER", payload: member });
            const firebaseToken = await authenticateWithFirebase(
              member,
              client.auth.getTokens()
            );
            localStorage.setItem(
              "firebase_session",
              JSON.stringify(firebaseToken)
            );
            dispatch({ type: "SET_FIREBASE_TOKENS", payload: firebaseToken });
            dispatch({ type: "SET_FIREBASE_LOADING", payload: false });

            return { success: true };
          }
        } catch (memberError) {
          safeLogObject("CAUGHT memberError", memberError);

          // Try a different approach - use the token to directly authenticate with Firebase
          // This can work if you have a Firebase custom token flow set up
          try {
            // Create a minimal user object with available information
            const mockUser = {
              isAuthenticated: true,
              member: {
                email: fallbackEmail || "unknown@example.com",
                _id: "session-token-user",
              },
            };

            // Try Firebase authentication with minimal info
            const firebaseToken = await authenticateWithFirebase(mockUser, {
              accessToken: sessionToken,
            });

            if (firebaseToken) {
              dispatch({ type: "SET_WIX_USER", payload: mockUser });
              localStorage.setItem(
                "firebase_session",
                JSON.stringify(firebaseToken)
              );
              dispatch({ type: "SET_FIREBASE_TOKENS", payload: firebaseToken });
              return { success: true, partialAuth: true };
            }
          } catch (firebaseError) {
            safeLogObject("Firebase fallback auth error", firebaseError);
          }

          return {
            success: false,
            error: "Authentication failed with the provided token.",
          };
        }
      } catch (error) {
        safeLogObject("Session token error", error);
        dispatch({ type: "SET_WIX_ERROR", payload: error });
        return { success: false, error: error.message };
      } finally {
        dispatch({ type: "SET_WIX_LOADING", payload: false });
        dispatch({ type: "SET_FIREBASE_LOADING", payload: false });
      }
    },
    [authenticateWithFirebase, client, safeLogObject]
  );

  // Utility functions for UI (connection duration and token expiry)
  const getConnectionDuration = useCallback(() => {
    if (!state.connectionTime) return "Not connected";
    const now = new Date();
    const diffMs = now - state.connectionTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    return diffHrs > 0
      ? `${diffHrs} hours, ${diffMins % 60} minutes`
      : `${diffMins} minutes`;
  }, [state.connectionTime]);

  const formatExpiryTime = useCallback(() => {
    if (!state.tokenExpiry) return "Unknown";
    return state.tokenExpiry.toLocaleTimeString();
  }, [state.tokenExpiry]);

  // Memoize context value
  const value = useMemo(
    () => ({
      // Access state directly here for values being passed down
      user: state.wixUser,
      tokens: state.wixTokens,
      wixLoading: state.wixLoading,
      wixError: state.wixError,
      firebaseUser: state.firebaseUser,
      firebaseToken: state.firebaseToken,
      firebaseLoading: state.firebaseLoading,
      firebaseError: state.firebaseError,
      firebaseConnected: state.firebaseConnected,
      tokenExpiry: state.tokenExpiry,
      connectionTime: state.connectionTime,
      // Calculated/Combined values
      isAuthenticated: !!(
        state.wixUser &&
        state.wixUser.member &&
        state.wixTokens?.accessToken?.value
      ),
      loading: state.wixLoading || state.firebaseLoading,
      error: state.wixError || state.firebaseError,
      isFirebaseConnected: state.firebaseConnected,
      // Functions (already memoized via useCallback)
      getConnectionDuration,
      formatExpiryTime,
      refreshFirebaseToken,
      login,
      signup,
      logout,
      processLoginCallback,
      client,
      handleSessionToken,
    }),
    // List all specific state variables used inside useMemo and all stable functions/objects
    [
      state, // Depending on the whole state object is often simplest
      client,
      login,
      signup,
      logout,
      processLoginCallback,
      handleSessionToken,
      refreshFirebaseToken,
      getConnectionDuration,
      formatExpiryTime,
      // Explicitly list state vars if removing 'state' dependency (more complex)
      // state.wixUser, state.wixTokens, state.wixLoading, state.wixError, state.firebaseUser, ...etc
    ]
  );

  return (
    <CombinedAuthContext.Provider value={value}>
      {children}
    </CombinedAuthContext.Provider>
  );
};

export { CombinedAuthContext };
export default CombinedAuthContext;
